import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, UserPlus } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { ChangeMemberRoleDialog } from "@/shared/organizations/ChangeMemberRoleDialog";
import { InlineNotice, type Notice } from "@/shared/organizations/InlineNotice";
import type { UserDetail } from "../types";
import { AddToOrganizationDialog, type AddToOrganizationOutcome } from "./AddToOrganizationDialog";
import { MembershipCard } from "./MembershipCard";
import { MembershipsSummary } from "./MembershipsSummary";
import { StaffBulkActions } from "./StaffBulkActions";
import { StaffManageDialogs } from "./StaffManageDialogs";
import { staffLocationRows } from "./membership-rows";
import { useAssignmentSelection } from "./use-assignment-selection";
import { useStaffBulkUpdate } from "./use-staff-bulk-update";
import { useUserMemberships } from "@/shared/organizations/use-user-memberships";
import { MembershipRole, type AdminMembership } from "@/shared/organizations/membership-types";

interface MembershipsSectionProps {
  readonly user: UserDetail;
  /** `org.members.manage` writes (add, bulk assignments); `org.members.read` alone is read-only. */
  readonly canManage: boolean;
  /** `org.admins.manage`: moving a member between STAFF and ADMIN, and granting ADMIN when adding. */
  readonly canChangeRoles?: boolean;
}

function MembershipsStatus({
  isError,
  isEmpty,
  canManage,
}: {
  readonly isError: boolean;
  readonly isEmpty: boolean;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      {isError && (
        <p role="alert" data-testid="memberships-error" className="text-sm text-destructive">
          {t("users.memberships.loadError")}
        </p>
      )}
      {isEmpty && (
        <p data-testid="memberships-empty" className="text-sm text-muted-foreground">
          {t(canManage ? "users.memberships.empty" : "users.memberships.emptyReadOnly")}
        </p>
      )}
    </>
  );
}

/**
 * Organizations and memberships card on the user-detail page (ADR-0017,
 * generalizes the ADR-0015 D5 staff card). Every membership of the user is
 * listed, whatever its role (OWNER / ADMIN / STAFF) or status, from
 * `GET /api/admin/users/:id/memberships`. Admins can add the user to any
 * organization (no invitation), move an ADMIN or STAFF row to the other role
 * in place, and bulk deactivate / reactivate / remove STAFF locations.
 *
 * None of this depends on the legacy `users.role` label: the memberships
 * payload decides. Only a WanderFit-internal account is excluded.
 *
 * Precondition: mount it only when the viewer holds `org.members.read` — the
 * read it issues needs that permission (a FINANCE viewer would get a 403).
 */
export function MembershipsSection({
  user,
  canManage,
  canChangeRoles = false,
}: MembershipsSectionProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useUserMemberships(user.id);

  const rows = useMemo(() => staffLocationRows(data), [data]);
  const selection = useAssignmentSelection(rows);
  const bulk = useStaffBulkUpdate(user.id, selection.selectedRows, selection.clear);
  const [addOpen, setAddOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState<AdminMembership | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const isEmpty = Boolean(data) && data?.memberships.length === 0;

  // The card exists only once the payload says what the account is: nothing
  // visible while loading (a screen-reader status only), so a WanderFit
  // account — which never belongs to organizations — never flashes the card.
  if (isLoading && !data) {
    return (
      <div
        role="status"
        aria-label={t("users.memberships.loading")}
        data-testid="memberships-loading"
        className="sr-only"
      />
    );
  }
  if (data && data.accountType === "INTERNAL") return null;

  const handleAdded = (outcome: AddToOrganizationOutcome) => {
    bulk.clearError();
    const key = {
      added: "organizationAccess.add.success",
      assigned: "organizationAccess.add.assigned",
      assignedNoChange: "organizationAccess.add.assignedNoChange",
    }[outcome];
    setNotice({ tone: "success", message: t(key) });
  };

  const currentRole = roleTarget?.role === MembershipRole.ADMIN ? "ADMIN" : "STAFF";

  return (
    <section
      data-testid="user-detail-memberships"
      className="space-y-4 rounded-lg border border-border bg-card p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="brand-eyebrow flex items-center gap-2">
          <Building2 className="h-4 w-4" aria-hidden="true" />
          {t("users.memberships.title")}
        </h2>
        {canManage && data && (
          <Button
            data-testid="membership-open-add"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
          >
            <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("organizationAccess.add.action")}
          </Button>
        )}
      </div>

      {notice && <InlineNotice notice={notice} testId="memberships-notice" />}

      <MembershipsStatus isError={isError} isEmpty={isEmpty} canManage={canManage} />

      {data && data.memberships.length > 0 && <MembershipsSummary memberships={data.memberships} />}

      {data && data.memberships.length > 0 && (
        <ul className="space-y-3" aria-label={t("users.memberships.listLabel")}>
          {data.memberships.map((m) => (
            <MembershipCard
              key={m.id}
              membership={m}
              user={user}
              canManage={canManage}
              selected={selection.selectedIds}
              onToggle={selection.toggle}
              canChangeRole={canChangeRoles}
              onChangeRole={setRoleTarget}
            />
          ))}
        </ul>
      )}

      {canManage && rows.length > 0 && (
        <StaffBulkActions
          selectedCount={selection.selectedRows.length}
          isPending={bulk.isPending}
          onRun={(action) => void bulk.run(action)}
          onRequestRemove={() => bulk.setRemoveOpen(true)}
        />
      )}

      {bulk.error && !bulk.removeOpen && (
        <p role="alert" data-testid="staff-bulk-error" className="text-sm text-destructive">
          {bulk.error}
        </p>
      )}

      {canManage && (
        <>
          <StaffManageDialogs
            selectedRows={selection.selectedRows}
            removeOpen={bulk.removeOpen}
            onRemoveOpenChange={bulk.setRemoveOpen}
            removePending={bulk.isPending}
            removeError={bulk.error}
            onConfirmRemove={() => void bulk.run("remove")}
          />
          <AddToOrganizationDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            userId={user.id}
            memberships={data?.memberships ?? []}
            canGrantAdmin={canChangeRoles}
            onDone={handleAdded}
          />
        </>
      )}

      {/* Mounted on org.admins.manage alone; ADMIN -> STAFF also needs org.members.manage (canManage) for its locations. */}
      {canChangeRoles && roleTarget && (
        <ChangeMemberRoleDialog
          open
          onOpenChange={(open) => {
            if (!open) setRoleTarget(null);
          }}
          organizationId={roleTarget.organization.id}
          membershipId={roleTarget.id}
          memberName={user.displayName || user.email}
          currentRole={currentRole}
          canAssignLocations={canManage}
          onDone={(newRole) =>
            setNotice({
              tone: "success",
              message: t(`organizationAccess.changeRole.success.${newRole}`),
            })
          }
          onStale={(message) => setNotice({ tone: "error", message })}
        />
      )}
    </section>
  );
}
